import { Injectable } from '@angular/core';
import {
  TenantExportDownloadResponse,
  TenantExportResponse,
  TenantExportsResponse,
} from '../models/tenant-export';
import { AuthService } from './auth.service';
import { EnvironmentService } from './environment.service';

export type StartTenantExportOptions = {
  includeFiles?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor(
    private envService: EnvironmentService,
    private authService: AuthService,
  ) {}

  public async startExport(
    customerId: string,
    options?: StartTenantExportOptions,
  ): Promise<TenantExportResponse> {
    return this.exportsRequest('POST', customerId, '', options);
  }

  public async getExports(customerId: string): Promise<TenantExportsResponse> {
    return this.exportsRequest('GET', customerId, '');
  }

  public async getExport(
    customerId: string,
    exportId: string,
  ): Promise<TenantExportResponse> {
    return this.exportsRequest('GET', customerId, `/${exportId}`);
  }

  public async cancelExport(
    customerId: string,
    exportId: string,
  ): Promise<TenantExportResponse> {
    return this.exportsRequest('POST', customerId, `/${exportId}/cancel`);
  }

  public async getDownloadUrl(
    customerId: string,
    exportId: string,
    fileName: string,
  ): Promise<TenantExportDownloadResponse> {
    return this.exportsRequest(
      'GET',
      customerId,
      `/${exportId}/download?file=${encodeURIComponent(fileName)}`,
    );
  }

  private async exportsRequest<T>(
    method: 'GET' | 'POST',
    customerId: string,
    urlAddition: string,
    body?: unknown,
  ): Promise<T> {
    const config = this.envService.getConfig();
    const init: RequestInit = {
      method,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + (await this.authService.getToken()),
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    };
    const res = await fetch(
      `${config.baseUrl}/customers/${customerId}/exports${urlAddition}`,
      init,
    );
    if (!res.ok) {
      const responseJson = await res.json().catch(() => undefined);
      const message = Array.isArray(responseJson?.message)
        ? responseJson.message.join(', ')
        : (responseJson?.message ?? res.statusText);
      throw new Error(`${res.status} - ${message}`);
    }
    return (await res.json()) as T;
  }
}
